import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { findAppointment } from '../services/squareApi';

const { FiPhone, FiMail, FiSearch } = FiIcons;

function WelcomePage({ onNavigate, setIsLoading, setError, error }) {
  const [lookupMethod, setLookupMethod] = useState('Phone Number');
  const [customerLookup, setCustomerLookup] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerLookup.trim()) {
      setError('Please enter your phone number or email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await findAppointment(customerLookup.trim());
      if (result.success) {
        onNavigate('appointment_lookup', {
          customer: result.customer,
          appointment: result.appointment
        });
      } else {
        setError(result.error || 'No appointment found. Please check your information and try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again or call us for assistance.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="text-center"
      >
        <div className="text-6xl mb-4">👋</div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          Welcome to Chef KnifeWorks!
        </h2>
        <p className="text-slate-600 leading-relaxed">
          Thanks for dropping by! Please check in so Jason knows your knives have arrived.
        </p>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            How would you like to check in?
          </label>
          <div className="grid grid-cols-2 gap-3">
            {['Phone Number', 'Email Address'].map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => setLookupMethod(method)}
                className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                  lookupMethod === method
                    ? 'border-[#0C1C35] bg-[#0C1C35] text-white'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <SafeIcon 
                  icon={method === 'Phone Number' ? FiPhone : FiMail} 
                  className="w-4 h-4" 
                />
                <span className="text-sm font-medium">
                  {method === 'Phone Number' ? 'Phone' : 'Email'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Enter your {lookupMethod.toLowerCase()}:
          </label>
          <input
            type={lookupMethod === 'Phone Number' ? 'tel' : 'email'}
            value={customerLookup}
            onChange={(e) => setCustomerLookup(e.target.value)}
            placeholder={lookupMethod === 'Phone Number' ? '555-123-4567' : 'name@email.com'}
            className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0C1C35] focus:border-transparent transition-all"
            required
          />
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
          >
            {error}
          </motion.div>
        )}

        <button
          type="submit"
          className="w-full bg-[#0C1C35] text-white py-4 rounded-xl font-medium hover:bg-blue-900 transition-colors flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiSearch} className="w-5 h-5" />
          Find My Appointment
        </button>
      </form>
    </div>
  );
}

export default WelcomePage;